import type { FormControlProps } from "@chakra-ui/react";
import { Select } from "@equidefi/ui/forms";
import type { TOption } from "@equidefi/ui/forms/Select";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";

export type SmartSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  label?: string;
  options: TOption[];
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  controlProps?: FormControlProps;
};

function SmartSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, label, options, placeholder, isRequired, isDisabled, controlProps }: SmartSelectProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    name,
    control,
  });

  const selected = options.find((option) => option.value === field.value) ?? null;

  return (
    <Select
      name={field.name}
      label={label}
      options={options}
      value={selected}
      onChange={(option) => field.onChange((option as TOption | null)?.value ?? null)}
      isInvalid={!!error}
      error={error?.message}
      isRequired={isRequired}
      isDisabled={isDisabled}
      placeholder={placeholder}
      controlProps={controlProps}
    />
  );
}

export default SmartSelect;
